import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function HeroSection() {
  const lineRefs = useRef<(HTMLElement | null)[]>([]);
  const ctaRef = useRef<HTMLDivElement>(null);
  const eyebrowRef = useRef<HTMLParagraphElement>(null);
  const subRef = useRef<HTMLParagraphElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Staggered entrance animations
    const elements = [eyebrowRef.current, ...lineRefs.current, subRef.current, ctaRef.current, statsRef.current];
    elements.forEach((el, i) => {
      if (!el) return;
      el.style.opacity = '0';
      el.style.transform = 'translateY(40px)';
      setTimeout(() => {
        if (!el) return;
        el.style.transition = 'opacity 1.1s cubic-bezier(0.16, 1, 0.3, 1), transform 1.1s cubic-bezier(0.16, 1, 0.3, 1)';
        el.style.opacity = '1';
        el.style.transform = 'translateY(0)';
      }, 200 + i * 180);
    });

    // Subtle parallax on scroll
    const handleScroll = () => {
      if (!bgRef.current) return;
      const scrolled = window.scrollY;
      bgRef.current.style.transform = `scale(1.08) translateY(${scrolled * 0.25}px)`;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
      {/* Cinematic background with parallax */}
      <div
        ref={bgRef}
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage:
            'url(https://public.readdy.ai/ai/img_res/45e0d7dcb8f63c6f40ecc93ed7a95dec.jpg)',
          transform: 'scale(1.08)',
          willChange: 'transform',
        }}
      />

      {/* Layered cinematic overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/45 to-black/85" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-black/30" />

      {/* Bottom fade into next section */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-charcoal to-transparent z-10" />

      {/* Hero content */}
      <div className="relative z-20 w-full text-center px-4 md:px-8 max-w-6xl mx-auto pt-28 pb-12">

        {/* Eyebrow */}
        <p ref={eyebrowRef} className="text-[10px] tracking-[0.55em] uppercase text-gold mb-10 flex items-center justify-center gap-3">
          <span className="w-8 h-px bg-gold/50" />
          Lake Tahoe &nbsp;&bull;&nbsp; Sierra Nevada &nbsp;&bull;&nbsp; Alpine County
          <span className="w-8 h-px bg-gold/50" />
        </p>

        {/* H1 — massive luxury serif */}
        <h1 className="font-serif font-light text-white leading-[1.0] mb-0">
          <span
            ref={el => { lineRefs.current[0] = el; }}
            className="block text-6xl md:text-8xl lg:text-[7rem] xl:text-[8.5rem] tracking-tight"
          >
            Luxury Real Estate
          </span>
          <span
            ref={el => { lineRefs.current[1] = el; }}
            className="block text-6xl md:text-8xl lg:text-[7rem] xl:text-[8.5rem] italic text-gold mt-1"
          >
            Representation
          </span>
          <span
            ref={el => { lineRefs.current[2] = el; }}
            className="block text-2xl md:text-3xl lg:text-4xl font-light text-white/65 mt-5 tracking-wide"
          >
            in Lake Tahoe &amp; Sierra Nevada
          </span>
        </h1>

        {/* Ornamental divider */}
        <div className="flex items-center justify-center gap-4 mt-8 mb-8">
          <div className="w-20 h-px bg-gradient-to-r from-transparent to-gold/60" />
          <div className="w-2 h-2 rotate-45 border border-gold/70" />
          <div className="w-2 h-2 rotate-45 bg-gold/60" />
          <div className="w-2 h-2 rotate-45 border border-gold/70" />
          <div className="w-20 h-px bg-gradient-to-l from-transparent to-gold/60" />
        </div>

        {/* Sub */}
        <p ref={subRef} className="text-white/65 text-base md:text-lg font-light leading-relaxed max-w-2xl mx-auto mb-12">
          Exclusive properties. Strategic positioning. Expert guidance. Representing buyers and sellers
          with precision, discretion, and results-driven execution across the premier alpine luxury market.
        </p>

        {/* CTAs */}
        <div ref={ctaRef} className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
          <a
            href="#consultation"
            className="whitespace-nowrap px-10 py-5 bg-gold text-white text-[11px] tracking-[0.28em] uppercase font-medium cursor-pointer rounded-md hover:bg-[#b8983a] transition-all duration-300 w-full sm:w-auto"
            style={{ boxShadow: '0 0 40px rgba(196,169,98,0.35)' }}
          >
            Schedule a Private Consultation
          </a>
          <Link
            to="/services"
            className="whitespace-nowrap px-10 py-5 border border-white/40 text-white text-[11px] tracking-[0.28em] uppercase font-light cursor-pointer rounded-md hover:border-gold/60 hover:bg-white/8 transition-all duration-300 w-full sm:w-auto"
          >
            View Services
          </Link>
        </div>

        {/* Floating credentials */}
        <div ref={statsRef} className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
          {[
            { val: '20+', label: 'Years Experience' },
            { val: '$500M+', label: 'In Sales' },
            { val: '500+', label: 'Clients Served' },
            { val: '5★', label: 'Rating' },
          ].map((s) => (
            <div key={s.label} className="text-center">
              <p className="font-serif text-2xl md:text-3xl text-gold font-light leading-none">{s.val}</p>
              <p className="text-[9px] tracking-[0.25em] uppercase text-white/40 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 text-white/30">
        <span className="text-[9px] tracking-[0.4em] uppercase">Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-white/30 to-transparent" />
      </div>
    </section>
  );
}
